import bsUtils from 'bootstrap/js/src/util';
import ReactiveComponent, { computed, watch } from '@utils/reactive-component';
import isFn from '@utils/is-function';
import AjaxLoader from '../ajax-loader/ajax-loader';
import AutoshipSelector from './cart-autoship';
import QuantitySelector from '../quantity-selector/quantity-selector';
import SelectBox from '../select-box/select-box';
import template, { iconStamp, listItem } from './templates/cart-line';
import { defaults } from './cart-mapping';
import priceString from '@utils/price-string';
import store from '@stores/cart/cart-store';
import { exceptionBehaviorValues } from '@stores/cart/cart-store';

const URL_IMAGE_NOT_AVAILABLE = '/images-2/image-not-available.svg';

const CLASS_NAME_CREATING = '-creating';
const CLASS_NAME_DESTROYED = '-destroyed';
const CLASS_NAME_OFF_SCREEN = '-off-screen';
const CLASS_NAME_TRANSITIONING = '-transitioning';

const DEFAULT_OPTIONS = {
    replaceOriginEl: false
};

export default class CartLine extends ReactiveComponent {
    #uiDisabled = false;

    constructor($originEl, context, data, options = DEFAULT_OPTIONS) {
        super();

        context = { ...defaults.context, ...context };
        options = { ...DEFAULT_OPTIONS, ...options };
        
        const $parsed = this.parseTemplate(template);
        this.$el = $parsed;
        this.$originEl = $originEl;

        // static labels
        this.$priceLabel = this.$el.find('[data-label="price"]');
        this.$removeLabel = this.$el.find('[data-label="remove"]');
        this.$updateLabel = this.$el.find('[data-label="update"]');

        // dynamic line/product data
        this.$attributes = this.$el.find('[data-region="attributes"]');
        this.$image = this.$el.find('[data-region="image"]');
        this.$name = this.$el.find('[data-region="name"]');
        this.$petSelector = this.$el.find('[data-region="petSelector"]');
        this.$price = this.$el.find('[data-region="price"]');
        this.$strikethrough = this.$el.find('[data-region="strikethrough"]');
        this.$productUrl = this.$el.find('[data-region="productUrl"]');

        // ui controls
        this.$removeCtrl = this.$el.find('[data-control="remove"]');
        this.$updateCtrl = this.$el.find('[data-control="update"]');

        // placeholders
        this.$autoshipSelector = this.$el.find('[data-placeholder="autoship"]');
        this.$autoshipFrequencySelectorPlaceholder = this.$el.find('[data-placeholder="autoshipFrequencySelector"]');
        this.$loaderPlaceholder = this.$el.find('[data-placeholder="loader"]');
        this.$prescriptionPlaceholder = this.$el.find('[data-placeholder="prescription"]');
        this.$promotionsPlaceholder = this.$el.find('[data-placeholder="promotions"]');
        this.$quantityPlaceholder = this.$el.find('[data-placeholder="quantity"]');

        // backorder
        this.$outofstockLabel = this.$el.find('[data-label="outofstock"]');
        this.$outofstockIcon = this.$el.find('[data-label="outofstockicon"]');
        this.$outofstockRegion = this.$el.find('[data-region="outofstockregion"]');

        // dynamic components
        this.autoshipSelector = null;
        this.loader = null;
        this.petSelector = null;
        this.quantitySelector = null;

        this.replaceOrigin = options.replaceOriginEl;
        this.context = context;
        this.data = data;
        this.lineId = this.data.lineId;

        //console.log(this.data);

        this.cachedLineValues = {
            autoshipFrequency: this.data.autoshipFrequency,
            petId: this.data.petId,
            quantity: this.data.quantity,
            backOrdered: this.data.backOrdered
        };

        this.reactive(this.cachedLineValues);

        this.init();
    }

    [watch]() {
        return {
            autoshipFrequency: () => this.setPendingUpdate(),
            petId: () => this.setPendingUpdate(),
            quantity: () => this.setPendingUpdate()
        }
    }

    [computed]() {
        return {
            isBackOrdered() {
                return this.backOrdered
            },
            hasPendingUpdate() {
                for(const prop of Object.keys(this.cachedLineValues)) {
                    if(this[prop] !== this.cachedLineValues[prop]) {
                        return true;
                    }
                }

                return false;
            }
        }
    }

    init() {
        this.renderLabels();
        this.renderRegions();
        this.renderIconStamps();
        this.setupLoader();
        if(!this.isBackOrdered){
            this.setupAutoshipSelector();
            this.setupPetSelector();
        }
        this.setupQuantitySelector();
        this.setupListeners();
    }

    cleanupCreate() {
        this.$el.removeClass(CLASS_NAME_TRANSITIONING).removeAttr('style');
    }

    cleanupDestroy() {
        this.$el.addClass(CLASS_NAME_DESTROYED).removeAttr('style');
        this.$el.empty();
    }

    async create() {
        this.$el.addClass([ CLASS_NAME_TRANSITIONING, CLASS_NAME_OFF_SCREEN ].join(' ')).css('display', 'none');
        const transitionTime = bsUtils.getTransitionDurationFromElement(this.$el.eq(0));

        await this.creating(transitionTime);

        this.$el.removeClass([ CLASS_NAME_CREATING ].join(' '));

        setTimeout(() => {
            this.cleanupCreate();
        }, transitionTime);
    }

    creating(transitionTime) {
        return new Promise((resolve) => {
            this.$el.slideDown(transitionTime, resolve);
            this.$el.removeClass(CLASS_NAME_CREATING);
        });
    }
    

    async destroy() {
        this.$el.addClass(CLASS_NAME_TRANSITIONING);
        const transitionTime = bsUtils.getTransitionDurationFromElement(this.$el.eq(0));

        await this.destroying(transitionTime);

        this.cleanupDestroy();

        setTimeout(() => {
            this.$el.remove();
        }, transitionTime);
    }

    destroying(transitionTime) {
        return new Promise((resolve) => {
            const height = this.$el.height();
            this.$el.addClass(CLASS_NAME_OFF_SCREEN).height(height);

            setTimeout(resolve, transitionTime);
        });
    }

    enableUpdateCtrl() {
        this.$updateCtrl
            .removeClass('disabled')
            .addClass('active')
            .attr('aria-disabled', null);
    }

    disableUpdateCtrl() {
        this.$updateCtrl
            .addClass('disabled')
            .removeClass('active')
            .attr('aria-disabled', true);
    }

    hideUpdateCtrl() {
        this.$updateCtrl.hide();
    }

    enableHighlight() {
        this.autoshipSelector?.highlight(this.autoshipFrequency !== this.cachedLineValues.autoshipFrequency);
        this.petSelector?.highlight(this.petId !== this.cachedLineValues.petId);
        this.quantitySelector?.highlight(this.quantity !== this.cachedLineValues.quantity);
    }

    disableHighlight() {
        this.autoshipSelector?.highlight(false);
        this.petSelector?.highlight(false);
        this.quantitySelector?.highlight(false);
    }

    enableUiOverrides() {
        this.#uiDisabled = true;
        this.autoshipSelector?.disable((deferredFn) => this.onDisabledUpdate(deferredFn));
        this.petSelector?.disable((deferredFn) => this.onDisabledUpdate(deferredFn));
        this.quantitySelector?.disable((deferredFn) => this.onDisabledUpdate(deferredFn));
    }

    disableUiOverrides() {
        this.#uiDisabled = false;
        this.autoshipSelector?.enable();
        this.petSelector?.enable();
        this.quantitySelector?.enable();
    }

    onAutoshipUpdate(autoshipFrequency) {
        this.autoshipFrequency = autoshipFrequency;
    }

    async onDisabledUpdate(deferredFn) {
        const isSaved = await this.unsavedLinePrompt();

        if(isSaved && isFn(deferredFn)) {
            deferredFn();
        }
    }

    onPetUpdated(petId) {
        this.petId = petId;
    }

    onQuantityUpdated(quantity) {
        this.quantity = quantity;
    }

    onRemoveClick() {
        if(!this.#uiDisabled) {
            store.actions.removeLine(this.lineId);
        } else {
            this.onDisabledUpdate();
        }
    }

    onPetSelectionValidated() {
        if(store.getters.isPetInvalid(this.lineId)) {
            this.petSelector.setInvalid();
        } else {
            this.petSelector.removeValidation();
        }
    }

    async onUpdateClick(evt) {
        let cartLine = $(evt.currentTarget).closest('.cart-line');
        let selectBox = $(cartLine).find('.select-box__select[name="PetId"]');
        let selectOptionValue = $(cartLine).find('.select-box__select[name="PetId"] option:selected').val(); //Pet Name

        if (selectOptionValue == '') {
            $(selectBox).addClass('is-invalid');
            return;
        }

        if(this.hasPendingUpdate && store.getters.hasPendingUpdate && !store.getters.isLineUpdateDisabled(this.lineId) && selectOptionValue !== '') {

            const { success } = await store.actions.updateLine();
            if(success) {
                this.cachedLineValues = {
                    autoshipFrequency: this.autoshipFrequency,
                    petId: this.petId,
                    quantity: this.quantity
                };
            }
        }
    }

    renderAttributes() {
        const attrs = store.getters.lineAttributes(this.lineId, {
            flavor: this.context.Flavor,
            packaging: this.context.Packaging,
            strengthSize: this.context.StrengthSize,
            strengthSizeSupplies: this.context.StrengthSizeSupplies,
            rxForm: this.context.RxForm,
        });

        const attrHtmlStr =
            attrs
                .map((attr) => listItem(attr.label, attr.value))
                .join('');

        const parsed = $.parseHTML(attrHtmlStr);
        this.$attributes.prepend(parsed);
    }

    renderIconStamps() {
        if(this.data.rxRequired) {
            const rxStamp = iconStamp('/images-2/prescription.svg', this.context.PrescriptionRequired);
            this.$prescriptionPlaceholder.replaceWith(rxStamp);
        }

        // todo: render promotions
    }

    renderImage() {
        const url = this.data.image?.constrainedUrl || URL_IMAGE_NOT_AVAILABLE;

        const img = document.createElement('img');
        img.src = url;
        img.alt = this.data.image.alt || this.data.displayName;

        this.$image.attr('href',this.data.productUrl);
        this.$image.html(img);

    }

    renderLabels() {
        this.$priceLabel.html(this.context.Price);
        this.$removeLabel.html(this.context.Remove);
        if(this.isBackOrdered){
            this.$removeCtrl.addClass('text-color-vca-red');
        }
        this.$updateLabel.html(this.context.ApplyUpdates);
        this.$outofstockLabel.html(this.context.OutOfStock);
        let img = document.createElement('img');
        img.src = this.context.OutOfStockIcon;
        this.$outofstockIcon.html(img);
    }

    renderPrices() {
        const formattedListPrice = priceString(this.data.listPrice);
        const formattedExceptionPrice = priceString(this.data.exceptionAmount);

        switch(this.data.exceptionBehavior) {
            case exceptionBehaviorValues.STRIKE_THROUGH:
                this.$price.html(formattedExceptionPrice);
                this.$strikethrough.html(formattedListPrice);
                break;
            case exceptionBehaviorValues.REPLACE:
                this.$price.html(formattedExceptionPrice);
                this.$strikethrough.hide();
                break;
            default:
                this.$price.html(formattedListPrice);
                this.$strikethrough.hide();
        }
    }

    renderRegions() {
        this.$name.html(this.data.displayName);
        this.$productUrl.attr('href',this.data.productUrl)

        this.renderAttributes();
        this.renderImage();
        this.renderPrices();
        if(this.isBackOrdered) {
            this.$outofstockRegion.show();
            this.hideUpdateCtrl();
        }else{
            this.$outofstockRegion.hide();
        }
    }

    setPendingUpdate() {
        if(!store.getters.isLineUpdateDisabled(this.lineId)) {
            if(this.hasPendingUpdate) {
                store.mutations.SET_PENDING_UPDATE({
                    lineId: this.lineId,
                    autoshipFrequency: this.autoshipFrequency,
                    petId: this.petId,
                    quantity: this.quantity
                });
            } else {
                store.mutations.RESET_PENDING_UPDATE();
            }
        }
    }

    setupAutoshipSelector() {
        let variantIdHasNF = this.data.variantId.endsWith('NF');

        this.autoshipSelector = new AutoshipSelector(this.$autoshipSelector, this.context, {
            frequency: this.data.autoshipFrequency,
            replaceOriginEl: true
        });

        this.autoshipSelector.render();

        if(this.isBackOrdered) {
            this.autoshipSelector.addClass('disable');
        }

        if (variantIdHasNF) {
            let selectBox = this.autoshipSelector.optionsSelector.$select[0];
            selectBox.querySelector('option[value="0"]').remove();
        }

    }

    setupLoader() {
        this.loader = new AjaxLoader(this.$loaderPlaceholder, {
            aria: false,
            className: 'd-inline-flex h-100 align-items-center mr-2',
            size: 'xs',
            srText: this.context.LoadingLine?.replace('{0}', this.data.displayName) || 'Loading...'
        });

        this.loader.render();
    }

    setupListeners() {
        this.$removeCtrl.on('click', (evt) => this.onRemoveClick(evt));
        
        if(!this.isBackOrdered) {
            this.$updateCtrl.on('click', (evt) => this.onUpdateClick(evt));
            this.autoshipSelector.watchValue((...args) => this.onAutoshipUpdate(...args));
            this.petSelector?.watchValue((...args) => this.onPetUpdated(...args));
            this.quantitySelector.watchValue((...args) => this.onQuantityUpdated(...args));
            store.watch('hasPendingUpdate', (...args) => this.togglePendingState(...args));
            store.watch('loadingLine', (state) => this.toggleLoadingState(state.loadingLine));
            store.watch('petSelectionValidated', () => this.onPetSelectionValidated());
            store.watch('lines', () => this.onPetSelectionValidated());
        }
    }

    setupPetSelector() {
        const petOptions = this.context.Pets;
        if(petOptions && petOptions.length > 0) {
            const mappedOptions = petOptions.map(option => {
                return {
                    text: option.PetName,
                    value: option.PetId
                };
            });

            this.petSelector = new SelectBox(this.$petSelector, {
                ariaLabel: this.context.PetSelectorAriaLabel,
                id: `petSelector_${this.data.lineId}`,
                name: 'PetId',
                mods: '-txt-sm',
                options: mappedOptions,
                replaceOriginEl: false,
                required: true,
                unselectedText: this.context.PetSelector,
                value: this.data.petId
            });

            this.petSelector.render();
        }
    }

    setupQuantitySelector() {
        const labels = {
            increment: this.context.Increment,
            decrement: this.context.Decrement,
            inputAriaLabel: this.context.QuantitySelector
        };

        this.quantitySelector = new QuantitySelector(this.$quantityPlaceholder, {
            replaceOriginEl: true,
            value: this.data.quantity,
            backOrdered: this.backOrdered
        }, labels);
    }

    toggleLoadingState(lineId) {
        if(this.lineId === lineId) {
            this.loader.active = true;
            this.disableUpdateCtrl();
        } else {
            this.loader.active = false;
        }
    }

    togglePendingState(state, getters, hasPendingUpdate) {
        if(hasPendingUpdate) {
            if(!store.getters.isLineUpdateDisabled(this.lineId)) {
                this.enableUpdateCtrl();
                this.enableHighlight();
            } else {
                this.enableUiOverrides();
            }
        } else {
            this.disableUpdateCtrl();
            this.disableHighlight();
            this.disableUiOverrides();
        }
    }

    /** virtual prompt method - will be overridden most of the time */
    unsavedLinePrompt() {
        return new Promise(resolve => {
            const confirmed = window.confirm(this.context.ModalLineUpdateBody);
            if(confirmed) {
                store.actions.updateLine().then(({ success }) => resolve(success));
            } else {
                resolve(false);
            }
        });
    }

    parseTemplate(templateStr = template) {
        const parsed = $.parseHTML(templateStr);
        return $(parsed);
    }

    static extendPrompt(fn) {
        if(isFn(fn)) {
            const CartLineExtended = CartLine;
            CartLineExtended.prototype.unsavedLinePrompt = fn;
            return CartLineExtended;
        }
    }

    static extendTemplate(templateStr) {
        class CartLineExtended extends CartLine {};
        CartLineExtended.prototype.parseTemplate = CartLineExtended.prototype.parseTemplate.bind(this, templateStr);
        return CartLineExtended;
    }
}

